import type { ProcessedFileState, FileState } from '@atlaskit/media-client';

import { ArtifactFormat } from './artifact-format';
import type { MediaFileAttributes } from '../mediaviewer';
import { stringify } from 'query-string';

export class MediaFileAttributesFactory {
	static create(mediaViewerId = '', fileState: FileState, baseUrl: string): MediaFileAttributes {
		const getArtifactUrl = (processingFileState: ProcessedFileState, name: string) => {
			return (
				processingFileState.artifacts &&
				processingFileState.artifacts[name] &&
				processingFileState.artifacts[name].url
			);
		};

		const imageUrl = ({ mimeType, id }: ProcessedFileState): string | undefined => {
			if (mimeType === 'image/jpeg') {
				return `/file/${id}/image`;
			}

			return undefined;
		};

		let artifactFormat;
		const artifactUrl = (processingFileState: ProcessedFileState) => {
			artifactFormat = ArtifactFormat.fromProcessedFileState(processingFileState);
			return artifactFormat && getArtifactUrl(processingFileState, artifactFormat.name);
		};

		let resource;
		let video1280;
		let poster1280;
		let poster640;

		if (fileState.status === 'processed') {
			resource = imageUrl(fileState) || artifactUrl(fileState);
			video1280 = getArtifactUrl(fileState, 'video_1280.mp4');
			poster1280 = getArtifactUrl(fileState, 'poster_1280.jpg');
			poster640 = getArtifactUrl(fileState, 'poster_640.jpg');
		}
		const binary = `/file/${fileState.id}/binary`;
		resource = resource || binary;

		const additionalParams = {
			'max-age': 3600,
		};

		const paramsSeparator = resource.indexOf('?') > -1 ? '&' : '?';

		// If the processing status is empty means the observable resolved with error
		const isError = fileState.status === 'error';
		const type = isError
			? 'error'
			: fileState.status === 'processed'
				? (artifactFormat && artifactFormat.type) || fileState.mimeType
				: 'non-supported';

		return {
			id: mediaViewerId,
			src: `${baseUrl}${resource}${paramsSeparator}${stringify(additionalParams)}`,
			srcDownload: `${baseUrl}${binary}?dl=1`,
			type,
			title: fileState.status === 'processed' ? fileState.name : '',
			src_hd: video1280 && `${baseUrl}${video1280}`,
			poster: poster1280 ? `${baseUrl}${poster1280}` : poster640 && `${baseUrl}${poster640}`,
		};
	}

	static getUniqueMediaViewerId(fileState: FileState) {
		return `${fileState.id}-${fileState.occurrenceKey || 'no-occurrenceKey'}`;
	}

	static fromFileState(fileState: FileState, baseUrl: string): MediaFileAttributes {
		const mediaViewerId = MediaFileAttributesFactory.getUniqueMediaViewerId(fileState);
		return MediaFileAttributesFactory.create(mediaViewerId, fileState, baseUrl);
	}

	static fromFileItemList(items: Array<FileState>, baseUrl: string): Array<MediaFileAttributes> {
		return items.map((fileState) => MediaFileAttributesFactory.fromFileState(fileState, baseUrl));
	}
}
